/**
 * Kiosk session: in-memory cache + Keychain persistence.
 * Stores accessToken, refreshToken, kioskId, organizationId, deviceIdentifier.
 */

import * as Keychain from 'react-native-keychain';
import type { StoredKioskSession } from '../types/kiosk';

const SESSION_SERVICE = 'ark-kiosk-session';
const SESSION_ACCOUNT = 'kiosk-session';
const DEVICE_SERVICE = 'ark-kiosk-device-identifier';
const DEVICE_ACCOUNT = 'device-identifier';

let memory: StoredKioskSession | null = null;

function requireTrimmed(value: string, label: string): string {
  const trimmed = value.trim();
  if (!trimmed) throw new Error(`${label} is empty`);
  return trimmed;
}

function parseSession(raw: string): StoredKioskSession | null {
  try {
    const parsed = JSON.parse(raw) as Partial<StoredKioskSession>;
    const accessToken =
      typeof parsed.accessToken === 'string' ? parsed.accessToken.trim() : '';
    const refreshToken =
      typeof parsed.refreshToken === 'string' ? parsed.refreshToken.trim() : '';
    const deviceIdentifier =
      typeof parsed.deviceIdentifier === 'string'
        ? parsed.deviceIdentifier.trim()
        : '';
    const kioskId = Number(parsed.kioskId);
    const organizationId = Number(parsed.organizationId);
    if (
      !accessToken ||
      !refreshToken ||
      !deviceIdentifier ||
      !Number.isInteger(kioskId) ||
      kioskId <= 0 ||
      !Number.isInteger(organizationId) ||
      organizationId <= 0
    ) {
      return null;
    }
    return {
      accessToken,
      refreshToken,
      kioskId,
      organizationId,
      deviceIdentifier,
      kioskName:
        typeof parsed.kioskName === 'string' ? parsed.kioskName : null,
    };
  } catch {
    return null;
  }
}

export function setKioskTokens(input: {
  accessToken: string;
  refreshToken?: string | null;
  kioskId?: number;
  organizationId?: number;
  deviceIdentifier?: string;
  kioskName?: string | null;
}) {
  const accessToken = requireTrimmed(input.accessToken, 'Kiosk access token');
  const refreshToken =
    typeof input.refreshToken === 'string' ? input.refreshToken.trim() : '';
  const current = memory;
  const kioskId = input.kioskId ?? current?.kioskId;
  const organizationId = input.organizationId ?? current?.organizationId;
  const deviceIdentifier =
    (input.deviceIdentifier ?? current?.deviceIdentifier ?? '').trim();

  if (
    !kioskId ||
    !organizationId ||
    !deviceIdentifier ||
    !Number.isInteger(kioskId) ||
    !Number.isInteger(organizationId)
  ) {
    // Allow ephemeral in-memory access-only bootstrap for tests / mid-refresh.
    memory = {
      accessToken,
      refreshToken: refreshToken || current?.refreshToken || '',
      kioskId: kioskId && Number.isInteger(kioskId) ? kioskId : 0,
      organizationId:
        organizationId && Number.isInteger(organizationId) ? organizationId : 0,
      deviceIdentifier: deviceIdentifier || current?.deviceIdentifier || '',
      kioskName: input.kioskName ?? current?.kioskName ?? null,
    };
    return;
  }

  memory = {
    accessToken,
    refreshToken: refreshToken || current?.refreshToken || '',
    kioskId,
    organizationId,
    deviceIdentifier,
    kioskName: input.kioskName ?? current?.kioskName ?? null,
  };
}

/** @deprecated Prefer setKioskTokens */
export function setKioskAccessToken(token: string) {
  setKioskTokens({ accessToken: token, refreshToken: null });
}

export function getKioskAccessToken(): string | null {
  return memory?.accessToken ?? null;
}

export function getKioskRefreshToken(): string | null {
  const value = memory?.refreshToken?.trim();
  return value || null;
}

export function getKioskSession(): StoredKioskSession | null {
  return memory;
}

export function getKioskDeviceIdentifier(): string | null {
  const value = memory?.deviceIdentifier?.trim();
  return value || null;
}

export function getKioskId(): number | null {
  const id = memory?.kioskId;
  return id && id > 0 ? id : null;
}

export function clearKioskTokens() {
  memory = null;
}

/** @deprecated Prefer clearKioskTokens / clearPersistedKioskSession */
export function clearKioskAccessToken() {
  clearKioskTokens();
}

export function hasKioskAccessToken(): boolean {
  return Boolean(memory?.accessToken);
}

export function hasActivatedKioskSession(): boolean {
  return Boolean(
    memory?.accessToken &&
      memory?.refreshToken &&
      memory.kioskId > 0 &&
      memory.organizationId > 0 &&
      memory.deviceIdentifier,
  );
}

export async function persistKioskSession(
  session: StoredKioskSession,
): Promise<void> {
  const next: StoredKioskSession = {
    accessToken: requireTrimmed(session.accessToken, 'accessToken'),
    refreshToken: requireTrimmed(session.refreshToken, 'refreshToken'),
    kioskId: session.kioskId,
    organizationId: session.organizationId,
    deviceIdentifier: requireTrimmed(
      session.deviceIdentifier,
      'deviceIdentifier',
    ).slice(0, 255),
    kioskName: session.kioskName ?? null,
  };
  if (!Number.isInteger(next.kioskId) || next.kioskId <= 0) {
    throw new Error('kioskId must be a positive integer');
  }
  if (!Number.isInteger(next.organizationId) || next.organizationId <= 0) {
    throw new Error('organizationId must be a positive integer');
  }
  memory = next;
  await Keychain.setGenericPassword(SESSION_ACCOUNT, JSON.stringify(next), {
    service: SESSION_SERVICE,
  });
}

export async function loadPersistedKioskSession(): Promise<StoredKioskSession | null> {
  const credentials = await Keychain.getGenericPassword({
    service: SESSION_SERVICE,
  });
  if (!credentials || typeof credentials === 'boolean' || !credentials.password) {
    // Leave any in-memory session alone (tests / mid-session); cold start memory is already null.
    return null;
  }
  const session = parseSession(credentials.password);
  memory = session;
  return session;
}

export async function clearPersistedKioskSession(): Promise<void> {
  memory = null;
  await Keychain.resetGenericPassword({ service: SESSION_SERVICE }).catch(
    () => undefined,
  );
}

function createDeviceIdentifier(): string {
  const random =
    typeof globalThis.crypto?.randomUUID === 'function'
      ? globalThis.crypto.randomUUID()
      : `device-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  // Neutral prefix shared by kiosk activation and the employee app.
  // Kiosk vs mobile is decided by kioskId / attendance_source, not this string.
  return `ark-device-${random}`.slice(0, 255);
}

/**
 * Stable per-install device id. Created once and reused for activation + API headers.
 */
export async function getOrCreateDeviceIdentifier(): Promise<string> {
  const existing = await Keychain.getGenericPassword({
    service: DEVICE_SERVICE,
  });
  if (existing && typeof existing !== 'boolean' && existing.password?.trim()) {
    return existing.password.trim().slice(0, 255);
  }
  const next = createDeviceIdentifier();
  await Keychain.setGenericPassword(DEVICE_ACCOUNT, next, {
    service: DEVICE_SERVICE,
  });
  return next;
}

export async function getStoredDeviceIdentifier(): Promise<string | null> {
  const existing = await Keychain.getGenericPassword({
    service: DEVICE_SERVICE,
  });
  if (!existing || typeof existing === 'boolean') return null;
  const value = existing.password?.trim();
  return value ? value.slice(0, 255) : null;
}
